/**
 * imageUtils.ts
 * ─────────────────────────────────────────────────────────────────────────────
 * Centralised image URL helpers for inventory and site-wide images.
 *
 * Strategy:
 *  1. Supabase Storage URLs  → append Supabase Image Transform params
 *     (width, quality, format=webp) so the CDN serves a compressed copy.
 *  2. Readdy.ai search-image URLs → use buildImageUrl() for consistent sizing
 *  3. All other URLs          → returned as-is; the browser's native lazy-load
 *     and the <picture> / srcset attributes handle the rest.
 *
 * Supabase Image Transform docs:
 *   https://supabase.com/docs/guides/storage/serving/image-transformations
 */

// ─────────────────────────────────────────────────────────────────────────────
// STABLE DIFFUSION / READDY.AI IMAGE CONVENTION
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Canonical image sizes for consistent usage across all pages.
 * Use these size keys with buildImageUrl() to ensure predictable dimensions.
 */
export const IMAGE_SIZES = {
  /** 400×300 — Grid thumbnails, small cards */
  thumbnail: { width: 400, height: 300 },
  /** 800×600 — Standard content cards, featured sections */
  card: { width: 800, height: 600 },
  /** 1920×1080 — Full-height hero backgrounds */
  hero: { width: 1920, height: 1080 },
  /** 1920×600 — Short hero banners */
  'hero-short': { width: 1920, height: 600 },
  /** 1200×630 — Open Graph / social media preview images */
  og: { width: 1200, height: 630 },
  /** 400×400 — Square avatars, icons, small product images */
  square: { width: 400, height: 400 },
  /** 420×560 — Portrait product shots, team photos */
  portrait: { width: 420, height: 560 },
  /** 1200×400 — Wide banners, CTA strips */
  banner: { width: 1200, height: 400 },
} as const;

export type ImageSizeKey = keyof typeof IMAGE_SIZES;

/**
 * Valid orientation values for readdy.ai search-image API.
 */
export const IMAGE_ORIENTATIONS = {
  landscape: 'landscape',
  portrait: 'portrait',
  squarish: 'squarish',
} as const;

export type ImageOrientation = keyof typeof IMAGE_ORIENTATIONS;

/**
 * SEQ (Sequence ID) Naming Convention
 * ────────────────────────────────────────────────────────────────────────────
 * Format: [page-slug]-[section-slug]-[variant]
 *
 * Examples:
 *   - hero-bg-pnw-002          → Home hero background, Pacific NW theme, variant 2
 *   - financing-hero-v1        → Financing page hero, version 1
 *   - inventory-card-truck-003 → Inventory card, truck category, variant 3
 *   - blog-og-article-042      → Blog OG image, article 42
 *
 * Rules:
 *   1. Use lowercase with hyphens only
 *   2. Keep under 50 characters
 *   3. Include page context for easy identification
 *   4. Increment variant number when regenerating similar images
 *   5. Never reuse seq IDs — each must be globally unique
 */

/**
 * Builds a complete readdy.ai search-image URL with consistent sizing.
 *
 * @param query - Natural language description of the image (min 150 chars recommended)
 * @param sizeKey - One of the canonical IMAGE_SIZES keys
 * @param seq - Unique sequence ID following [page]-[section]-[variant] convention
 * @param orientation - Optional orientation hint (landscape/portrait/squarish)
 * @returns Fully-formed readdy.ai image URL
 *
 * @example
 * buildImageUrl(
 *   'Pacific Northwest mountain landscape with evergreen forests...',
 *   'hero',
 *   'home-hero-pnw-001',
 *   'landscape'
 * )
 * // → https://public.readdy.ai/ai/img_res/463565eecbb42f600b80101ba13f73f3.jpg
 */
export function buildImageUrl(
  query: string,
  sizeKey: ImageSizeKey,
  seq: string,
  orientation?: ImageOrientation
): string {
  const size = IMAGE_SIZES[sizeKey];
  const baseUrl = 'https://readdy.ai/api/search-image';
  const params = new URLSearchParams({
    query,
    width: size.width.toString(),
    height: size.height.toString(),
    seq,
  });

  if (orientation) {
    params.set('orientation', orientation);
  }

  return `${baseUrl}?${params.toString()}`;
}

/**
 * Shortcut for building Open Graph (1200×630) images.
 * Always uses 'landscape' orientation.
 *
 * @example
 * buildOgImageUrl('Used cars for sale in Warrenton Oregon...', 'inventory-og-v1')
 */
export function buildOgImageUrl(query: string, seq: string): string {
  return buildImageUrl(query, 'og', seq, 'landscape');
}

/**
 * Shortcut for building full-height hero backgrounds (1920×1080).
 * Always uses 'landscape' orientation.
 *
 * @example
 * buildHeroImageUrl('Pacific Northwest mountain landscape...', 'home-hero-pnw-002')
 */
export function buildHeroImageUrl(query: string, seq: string): string {
  return buildImageUrl(query, 'hero', seq, 'landscape');
}

/**
 * Shortcut for building short hero banners (1920×600).
 * Always uses 'landscape' orientation.
 *
 * @example
 * buildShortHeroImageUrl('Modern car dealership office...', 'financing-hero-v1')
 */
export function buildShortHeroImageUrl(query: string, seq: string): string {
  return buildImageUrl(query, 'hero-short', seq, 'landscape');
}

// ─────────────────────────────────────────────────────────────────────────────
// SUPABASE STORAGE IMAGE OPTIMIZATION
// ─────────────────────────────────────────────────────────────────────────────

const SUPABASE_STORAGE_PATTERN = /supabase\.co\/storage\/v1\/object\/(public|sign)\//;
const READDY_IMAGE_PATTERN = /readdy\.ai\/api\/search-image/;
const STATIC_READDY_PATTERN = /static\.readdy\.ai\/image\//;

export interface ImageTransformOptions {
  /** Target width in pixels */
  width?: number;
  /** JPEG/WebP quality 1-100 (default 80) */
  quality?: number;
  /** Output format — webp gives best compression */
  format?: 'webp' | 'avif' | 'origin';
  /** Resize mode */
  resize?: 'cover' | 'contain' | 'fill';
}

/**
 * Returns an optimised URL for a vehicle image.
 * For Supabase Storage URLs the transform query-string is appended.
 * For all other hosts the original URL is returned unchanged.
 */
export function optimiseImageUrl(
  url: string,
  opts: ImageTransformOptions = {}
): string {
  if (!url || url.startsWith('data:')) return url;

  if (SUPABASE_STORAGE_PATTERN.test(url)) {
    const { width = 800, quality = 80, format = 'webp', resize = 'cover' } = opts;
    // Supabase transform params go on the render endpoint
    // e.g. .../object/public/bucket/file.jpg?width=400&quality=75&format=webp
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}width=${width}&quality=${quality}&format=${format}&resize=${resize}`;
  }

  return url;
}

/**
 * Returns a thumbnail-sized URL (400 px wide, quality 70, webp).
 * Used for inventory grid cards where images are displayed at ~300-400 px.
 */
export function thumbnailUrl(url: string): string {
  return optimiseImageUrl(url, { width: 400, quality: 70, format: 'webp' });
}

/**
 * Returns a medium-sized URL (800 px wide, quality 80, webp).
 * Used for list-view cards and vehicle detail hero.
 */
export function mediumUrl(url: string): string {
  return optimiseImageUrl(url, { width: 800, quality: 80, format: 'webp' });
}

/**
 * Returns a full-resolution URL (1600 px wide, quality 85, webp).
 * Used for the lightbox / full-screen gallery.
 */
export function fullUrl(url: string): string {
  return optimiseImageUrl(url, { width: 1600, quality: 85, format: 'webp' });
}

/**
 * Replaces or sets the `width` parameter in a readdy.ai search-image URL.
 * Prevents duplicate width params that cause image doubling.
 */
function setReaddyImageWidth(url: string, width: number, quality?: number): string {
  const [base, qs] = url.split('?');
  const params = new URLSearchParams(qs || '');
  params.set('width', String(width));
  if (quality !== undefined) params.set('q', String(quality));
  params.set('fm', 'webp');
  return `${base}?${params.toString()}`;
}

/**
 * Appends WebP format to readdy.ai search-image URLs.
 * For Supabase URLs, delegates to optimiseImageUrl.
 * For all others, returns as-is.
 */
export function toWebP(url: string, width?: number, quality = 80): string {
  if (!url || url.startsWith('data:')) return url;
  if (SUPABASE_STORAGE_PATTERN.test(url)) {
    return optimiseImageUrl(url, { width: width || 800, quality, format: 'webp' });
  }
  if (READDY_IMAGE_PATTERN.test(url)) {
    return setReaddyImageWidth(url, width || 800, quality);
  }
  return url;
}

/**
 * Builds a <source type="image/webp"> srcSet string for any image URL.
 * Works for Supabase Storage, readdy.ai, static.readdy.ai, and plain URLs.
 */
export function buildWebPSrcSet(url: string): string {
  if (!url || url.startsWith('data:')) return url;

  if (SUPABASE_STORAGE_PATTERN.test(url)) {
    const w400 = optimiseImageUrl(url, { width: 400, quality: 70, format: 'webp' });
    const w800 = optimiseImageUrl(url, { width: 800, quality: 80, format: 'webp' });
    const w1200 = optimiseImageUrl(url, { width: 1200, quality: 85, format: 'webp' });
    return `${w400} 400w, ${w800} 800w, ${w1200} 1200w`;
  }

  if (READDY_IMAGE_PATTERN.test(url)) {
    const w400 = setReaddyImageWidth(url, 400, 70);
    const w800 = setReaddyImageWidth(url, 800, 80);
    const w1200 = setReaddyImageWidth(url, 1200, 85);
    return `${w400} 400w, ${w800} 800w, ${w1200} 1200w`;
  }

  if (STATIC_READDY_PATTERN.test(url)) {
    const sep = url.includes('?') ? '&' : '?';
    const w400 = `${url}${sep}width=400`;
    const w800 = `${url}${sep}width=800`;
    const w1200 = `${url}${sep}width=1200`;
    return `${w400} 400w, ${w800} 800w, ${w1200} 1200w`;
  }

  // Non-transformable: repeat same URL at all widths
  return `${url} 400w, ${url} 800w, ${url} 1200w`;
}

/**
 * Builds a hero-specific srcSet with 480w, 800w, 1200w, and 1920w variants.
 * Optimized for full-width hero images across all device sizes.
 * Mobile/tablet variants (480w, 800w) use quality=75 for faster loading.
 * Desktop variants (1200w, 1920w) use quality=85 for better visual quality.
 */
export function buildHeroSrcSet(url: string): string {
  if (!url || url.startsWith('data:')) return url;

  if (SUPABASE_STORAGE_PATTERN.test(url)) {
    const w480 = optimiseImageUrl(url, { width: 480, quality: 75, format: 'webp' });
    const w800 = optimiseImageUrl(url, { width: 800, quality: 75, format: 'webp' });
    const w1200 = optimiseImageUrl(url, { width: 1200, quality: 85, format: 'webp' });
    const w1920 = optimiseImageUrl(url, { width: 1920, quality: 85, format: 'webp' });
    return `${w480} 480w, ${w800} 800w, ${w1200} 1200w, ${w1920} 1920w`;
  }

  if (READDY_IMAGE_PATTERN.test(url)) {
    const w480 = setReaddyImageWidth(url, 480, 75);
    const w800 = setReaddyImageWidth(url, 800, 75);
    const w1200 = setReaddyImageWidth(url, 1200, 85);
    const w1920 = setReaddyImageWidth(url, 1920, 85);
    return `${w480} 480w, ${w800} 800w, ${w1200} 1200w, ${w1920} 1920w`;
  }

  if (STATIC_READDY_PATTERN.test(url)) {
    // static.readdy.ai supports ?width= and &q= parameters
    const sep = url.includes('?') ? '&' : '?';
    const w480 = `${url}${sep}width=480&q=75`;
    const w800 = `${url}${sep}width=800&q=75`;
    const w1200 = `${url}${sep}width=1200&q=85`;
    const w1920 = `${url}${sep}width=1920&q=85`;
    return `${w480} 480w, ${w800} 800w, ${w1200} 1200w, ${w1920} 1920w`;
  }

  // Non-transformable: repeat same URL at all widths
  return `${url} 480w, ${url} 800w, ${url} 1200w, ${url} 1920w`;
}

/** Inline SVG fallback shown when an image fails to load */
export const FALLBACK_IMAGE =
  'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="800" height="600" viewBox="0 0 800 600"%3E%3Crect fill="%23F7F5F0" width="800" height="600"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="system-ui" font-size="18" fill="%239AA8B8"%3ENo Image Available%3C/text%3E%3C/svg%3E';

// ─────────────────────────────────────────────────────────────────────────────
// FALLBACK IMAGE CONSTANTS
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Default fallback image for vehicle cards when no image is available.
 * Used in inventory grid/list views.
 */
export const VEHICLE_FALLBACK_IMAGE = buildImageUrl(
  'modern clean used car on white studio background professional automotive photography soft lighting minimal elegant',
  'card',
  'card-fallback-001',
  'landscape'
);

/**
 * Fallback image for broker "no results" section.
 * Shows professional car broker searching for vehicles at auction.
 */
export const BROKER_NO_RESULTS_IMAGE = buildImageUrl(
  'professional car broker searching for the perfect vehicle at an auto auction, wide selection of cars in background, confident advisor with clipboard, warm golden lighting, clean modern setting, no text, cinematic automotive photography',
  'banner',
  'broker-no-results-01',
  'landscape'
);

/**
 * Fallback image for similar vehicles section.
 * Generic modern vehicle on clean background.
 */
export const SIMILAR_VEHICLE_FALLBACK_IMAGE = buildImageUrl(
  'modern car vehicle automobile clean simple background professional product photography studio lighting neutral backdrop',
  'thumbnail',
  'similar-fallback-001',
  'landscape'
);

export const getOptimizedImageUrl = (
  url: string,
  options: {
    width?: number;
    height?: number;
    quality?: number;
    format?: string;
  } = {}
): string => {
  const { width, height, quality, format } = options;

  // Add cache-friendly parameters
  const params = new URLSearchParams();

  if (width) params.set('width', width.toString());
  if (height) params.set('height', height.toString());
  if (quality) params.set('quality', quality.toString());
  if (format) params.set('format', format);

  // Add cache version for better CDN caching
  params.set('v', '1');

  return `${url}?${params.toString()}`;
};

// Preload critical images
export const preloadImage = (src: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = src;
    link.crossOrigin = 'anonymous';
    link.onload = () => resolve();
    link.onerror = reject;
    document.head.appendChild(link);
  });
};